import { useState } from 'react';
import {
  Shield,
  Check,
  X,
  Clock,
  AlertTriangle,
  Loader2,
  History,
  Search,
} from 'lucide-react';
import { usePendingApprovals, type PendingApproval } from '@/hooks/usePendingApprovals';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface ApprovalHistoryItem {
  id: string;
  action_type: string;
  action_metadata: Record<string, unknown>;
  status: string;
  created_at: string;
  resolved_at: string | null;
  requester_name: string;
  approver_name: string | null;
}

export default function Approvals() {
  const { profile } = useAuth();
  const {
    pendingApprovals,
    isLoading,
    approve,
    deny,
    isApproving,
    isDenying,
    getActionDescription,
    formatActionType,
  } = usePendingApprovals();

  const [selectedApproval, setSelectedApproval] = useState<PendingApproval | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch approval history
  const { data: approvalHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['approval-history', profile?.organization_id],
    queryFn: async (): Promise<ApprovalHistoryItem[]> => {
      if (!profile?.organization_id) return [];

      const { data, error } = await supabase
        .from('owner_2fa_approvals')
        .select(`
          id,
          action_type,
          action_metadata,
          approved_at,
          requester:requester_user_id (first_name, last_name),
          approver:approver_user_id (first_name, last_name)
        `)
        .eq('organization_id', profile.organization_id)
        .order('approved_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching approval history:', error);
        return [];
      }

      return (data || []).map((item: Record<string, unknown>) => ({
        id: item.id as string,
        action_type: item.action_type as string,
        action_metadata: item.action_metadata as Record<string, unknown>,
        status: 'approved',
        created_at: item.approved_at as string,
        resolved_at: item.approved_at as string,
        requester_name: formatName(item.requester as { first_name?: string; last_name?: string }),
        approver_name: formatName(item.approver as { first_name?: string; last_name?: string }),
      }));
    },
    enabled: !!profile?.organization_id && activeTab === 'history',
  });

  function formatName(user?: { first_name?: string; last_name?: string }): string {
    if (!user) return 'Unknown';
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  }

  const handleApprove = async () => {
    if (!selectedApproval || !verificationCode) return;

    setError(null);
    try {
      await approve({ approvalId: selectedApproval.id, code: verificationCode });
      setSelectedApproval(null);
      setVerificationCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    }
  };

  const handleDeny = async (approval: PendingApproval) => {
    try {
      await deny(approval.id);
    } catch (err) {
      console.error('Failed to deny:', err);
    }
  };

  // Check if user has permission
  if (profile?.role !== 'owner' && profile?.role !== 'primary_admin') {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Shield className="h-12 w-12 text-gray-300 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Access Restricted
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Only owners and primary admins can view approval requests.
        </p>
      </div>
    );
  }

  // Filter approvals based on search
  const filteredApprovals = pendingApprovals.filter((approval) => {
    if (!searchTerm) return true;
    const description = getActionDescription(approval).toLowerCase();
    const actionType = formatActionType(approval.action_type).toLowerCase();
    const search = searchTerm.toLowerCase();
    return description.includes(search) || actionType.includes(search);
  });

  const filteredHistory = (approvalHistory || []).filter((item) => {
    if (!searchTerm) return true;
    const actionType = formatActionType(item.action_type).toLowerCase();
    const search = searchTerm.toLowerCase();
    return actionType.includes(search) || item.requester_name.toLowerCase().includes(search);
  });

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-3">
          <Shield className="h-7 w-7 text-green-600" />
          Approval Center
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Review and approve protected actions that require owner verification
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('pending')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2',
              activeTab === 'pending'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
            )}
          >
            <Clock className="h-4 w-4" />
            Pending
            {pendingApprovals.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-xs">
                {pendingApprovals.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2',
              activeTab === 'history'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
            )}
          >
            <History className="h-4 w-4" />
            History
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search approvals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm"
          />
        </div>
      </div>

      {/* Content */}
      {activeTab === 'pending' ? (
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredApprovals.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Check className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                All caught up!
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {searchTerm
                  ? 'No approvals match your search'
                  : 'No pending approvals at this time'}
              </p>
            </div>
          ) : (
            filteredApprovals.map((approval) => (
              <div
                key={approval.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
              >
                {selectedApproval?.id === approval.id ? (
                  // Verification Form
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Enter your 2FA code to approve
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {getActionDescription(approval)}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedApproval(null);
                          setVerificationCode('');
                          setError(null);
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {error && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        {error}
                      </div>
                    )}

                    <div className="flex items-center gap-4">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={verificationCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setVerificationCode(value);
                        }}
                        placeholder="000000"
                        className="w-48 px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-green-500"
                        autoFocus
                      />
                      <button
                        onClick={handleApprove}
                        disabled={verificationCode.length !== 6 || isApproving}
                        className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        {isApproving ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <Check className="h-5 w-5" />
                            Approve
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  // Approval Card
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 mb-2">
                          {formatActionType(approval.action_type)}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {getActionDescription(approval)}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400">
                          <span>Requested by {approval.requester_name || 'Unknown'}</span>
                          <span>-</span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(approval.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>

                      {/* Time remaining */}
                      <div className="text-right">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Expires</p>
                        <p className={cn(
                          'text-sm font-medium',
                          new Date(approval.expires_at) < new Date(Date.now() + 60000)
                            ? 'text-red-600'
                            : 'text-gray-900 dark:text-white'
                        )}>
                          {formatDistanceToNow(new Date(approval.expires_at))}
                        </p>
                      </div>
                    </div>

                    {/* Action metadata preview */}
                    {approval.action_metadata && Object.keys(approval.action_metadata).length > 0 && (
                      <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Details</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {Object.entries(approval.action_metadata)
                            .filter(([key]) => !['description'].includes(key))
                            .slice(0, 4)
                            .map(([key, value]) => (
                              <div key={key}>
                                <span className="text-gray-500 dark:text-gray-400 capitalize">
                                  {key.replace(/_/g, ' ')}:
                                </span>{' '}
                                <span className="text-gray-900 dark:text-white">
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={() => setSelectedApproval(approval)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                      >
                        <Check className="h-4 w-4" />
                        Approve with 2FA
                      </button>
                      <button
                        onClick={() => handleDeny(approval)}
                        disabled={isDenying}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <X className="h-4 w-4" />
                        Deny
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        // History Tab
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {historyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="p-12 text-center">
              <History className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No history matches your search' : 'No approval history yet'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredHistory.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        {formatActionType(item.action_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {item.requester_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {item.approver_name || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {item.resolved_at && format(new Date(item.resolved_at), 'MMM d, yyyy h:mm a')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
